import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../app/hooks';
import {
  Address,
  setNewServiceAddress,
  setServiceAddress,
} from '../features/serviceLocation/serviceLocationSlice';
import { setPostalCode } from '../features/postalCode/postalCodeSlice';
import { adoptHandoffIdentity, mp } from '../util/analytics';
import { isAperioTrackingEnabled } from '../util/aperioTracking';
import resolveAddressText, { PLACES_WAIT_MS } from '../util/placesLookup';
import {
  hasUsableHint,
  PlacesBiasHint,
  saveBiasHint,
} from '../util/placesBias';

/**
 * Hand-off from the marketing site (aperiomobility.com -> /book/location).
 *
 * The site appends its address widget's result to the booking URL:
 *
 *   /book/location?street=...&city=...&province=MB&province_name=Manitoba
 *     &postal=R3M%200A1&country=CA&country_name=Canada&lat=...&lng=...
 *     &place_id=...&address=<formatted>&lang=en&src=web-hero
 *     &mp_id=<mixpanel distinct id>[&mp_device=1]&utm_source=...&gclid=...
 *
 * The address params are the site's contract (aperio/site/CONTRACT.md) and
 * their names never change. `mp_id` and the utm_* / click ids are additions
 * the site appends only when the visitor accepted analytics there; they are
 * consumed and stripped here exactly like the rest.
 *
 * When the site could only send `address` (free text the visitor typed), plus
 * `lang` and `src`, it is because it had no working address provider of its
 * own. That must not cost the visitor a second round of typing: we run the
 * same Google Places lookup the address box uses on that text, take the first
 * prediction and put it into the store exactly as picking a suggestion would
 * (util/placesLookup.ts). Only if that finds nothing do we fall back to the
 * old behaviour - the text seeded in the box for the visitor to pick from.
 *
 * The hook reads the params once (synchronously, so the address box can be
 * seeded on its very first render), hydrates the store and then strips the
 * hand-off params from the URL so a refresh or Back never overwrites what the
 * visitor edits here. `consumeHandoff()` returns true exactly once, and only
 * when a complete address was hydrated - the Location route uses it to
 * auto-advance once the postal code has been checked against the service area.
 *
 * `checking` is true for as long as the hand-off is still being worked on, so
 * the route can show "Checking your address..." instead of flashing an address
 * form the visitor is never meant to fill in. It ends when `consumeHandoff()`
 * is called (the postal check came back), when the lookup finds nothing, or -
 * as a backstop, so nobody can ever be stuck looking at it - after
 * HANDOFF_TIMEOUT_MS.
 */

/**
 * Longest the route may show "Checking your address..." before giving the
 * visitor the form back. It covers the Places lookup (PLACES_WAIT_MS) plus the
 * service-area query that follows it.
 */
export const HANDOFF_TIMEOUT_MS = PLACES_WAIT_MS + 4000;

export const HANDOFF_SOURCE_KEY = 'bookingSource';
export const HANDOFF_LANG_KEY = 'bookingLang';

/**
 * Attribution the marketing site appends on top of the address params.
 *
 * `mp_id` is the visitor's Mixpanel distinct id on aperiomobility.com. Since
 * 2026-09-26 the site and this portal (on book.aperiomobility.com, after an
 * analytics accept) share Mixpanel's identity cookie on .aperiomobility.com
 * (util/analytics mixpanelPersistence), so the id normally arrives on its
 * own; `mp_id` stays as the fallback for when the cookie does not (an older
 * aperio.gooil.app link, cookies blocked). The site only appends it once the visitor has
 * accepted analytics there. Until they log in it is an anonymous DEVICE id -
 * `$device:<uuid>` as the SDK reports it, or the bare uuid with `mp_device=1`
 * from a site that strips the prefix - and a device id is registered as a
 * super property, never identified (see util/analytics adoptHandoffIdentity).
 *
 * The utm_* / click ids are the site's LAST-TOUCH campaign, so a booking event
 * carries the campaign even though this app never saw the ad click.
 */
const HANDOFF_MP_ID = 'mp_id';
/**
 * `cov=1`: the marketing site has already reported this address's coverage
 * (its address_serviceable), so this portal checks it again for itself but
 * does not report a second result (Location trackServiceability).
 */
const HANDOFF_COVERAGE_REPORTED = 'cov';
const HANDOFF_MP_DEVICE = 'mp_device';
const HANDOFF_CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'ndclid',
];

const HANDOFF_PARAMS = [
  'street',
  'postal',
  'city',
  'province',
  'province_name',
  'country',
  'country_name',
  'lat',
  'lng',
  'place_id',
  'address',
  'lang',
  'src',
  HANDOFF_COVERAGE_REPORTED,
  HANDOFF_MP_ID,
  HANDOFF_MP_DEVICE,
  ...HANDOFF_CAMPAIGN_PARAMS,
];

export interface Handoff {
  /** Fully resolved address, only when the site sent street + postal. */
  address?: Address;
  /** Google place_id of that address, when the site sent one. */
  placeId?: string | null;
  /** Free text to seed the address input with. */
  text: string;
  /** `src` attribution, e.g. `web-hero`. */
  source: string | null;
  /** `lang` the visitor was reading the marketing site in. */
  lang: string | null;
  /** The visitor's Mixpanel distinct id on the marketing site, if any. */
  mpId: string | null;
  /** True when the site said `mp_id` is a device id (`mp_device=1`). */
  mpIsDevice: boolean;
  /**
   * Where the visitor is, as far as the site could tell: the picked
   * address's coordinates, or at least its city and province. It biases the
   * address box's suggestions towards them (util/placesBias).
   */
  bias: PlacesBiasHint | null;
  /** utm_* / click ids the marketing site carried through. */
  campaign: Record<string, string>;
  /** The site already reported this check's result (`cov=1`). */
  siteReported: boolean;
  /** True when the visitor arrived from the marketing site. */
  present: boolean;
}

function readCampaign(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  HANDOFF_CAMPAIGN_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      out[key] = value;
    }
  });
  return out;
}

function readHandoff(params: URLSearchParams): Handoff {
  const street = params.get('street');
  const postal = params.get('postal');
  // Free text, or failing that the parts a link did send: a quote by postal
  // code, or an assistant's `street` + `city` with no postal, used to arrive
  // with nothing in the box and the visitor typed it all again (QA
  // 2026-10-27). Only street / postal seed it: `city` + `province` alone are
  // the marketing site's bias hint, not an address.
  const text = (params.get('address') || (street || postal
    ? [street, params.get('city'), params.get('province'), postal]
      .map((part) => (part || '').trim()).filter(Boolean).join(', ')
    : '')).trim();
  const source = params.get('src');
  const lang = params.get('lang');
  const mpId = params.get(HANDOFF_MP_ID);
  const mpIsDevice = params.get(HANDOFF_MP_DEVICE) === '1';
  const campaign = readCampaign(params);
  const present = !!(street || postal || text || source);
  const siteReported = params.get(HANDOFF_COVERAGE_REPORTED) === '1';

  const hintLat = parseFloat(params.get('lat') || '');
  const hintLng = parseFloat(params.get('lng') || '');
  const hint: PlacesBiasHint = {
    lat: Number.isFinite(hintLat) ? hintLat : null,
    lng: Number.isFinite(hintLng) ? hintLng : null,
    city: params.get('city'),
    province: params.get('province'),
  };
  const bias = hasUsableHint(hint) ? hint : null;

  if (!street || !postal) {
    return {
      text, source, lang, mpId, mpIsDevice, bias, campaign, present, siteReported,
    };
  }

  const countryCode = (params.get('country') || 'CA').toUpperCase();
  const lat = parseFloat(params.get('lat') || '');
  const lng = parseFloat(params.get('lng') || '');
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  const address: Address = {
    street,
    city: params.get('city') || '',
    postalCode: postal.toUpperCase(),
    provinceCode: params.get('province') || '',
    province: params.get('province_name') || params.get('province') || '',
    countryCode,
    country: params.get('country_name')
      || (countryCode === 'US' ? 'United States' : 'Canada'),
    ...(hasCoords && { lat, lng }),
  };

  return {
    address,
    placeId: params.get('place_id'),
    text: text || `${street}, ${address.city} ${address.postalCode}`,
    source,
    lang,
    mpId,
    mpIsDevice,
    bias,
    campaign,
    present: true,
    siteReported,
  };
}

export default function usePrefilledAddress() {
  const [params, setParams] = useSearchParams();
  const dispatch = useAppDispatch();
  // Snapshot the params on the first render: the effect below strips them.
  const [handoff] = useState(() => readHandoff(params));
  const pending = useRef(!!handoff.address);
  const applied = useRef(false);
  const alive = useRef(true);
  // The site sent an address of some kind, so the visitor is mid-flow and the
  // form is not what they should be looking at yet.
  const [checking, setChecking] = useState(
    () => handoff.present && (!!handoff.address || !!handoff.text),
  );
  // The text lookup came back empty: the visitor has to pick after all.
  const [needsManualPick, setNeedsManualPick] = useState(false);

  useEffect(() => () => { alive.current = false; }, []);

  // Backstop. Nothing below is allowed to leave someone staring at
  // "Checking your address..." - not a Places script that never loads, not a
  // service-area query that never answers.
  useEffect(() => {
    if (!checking) {
      return undefined;
    }
    const timer = setTimeout(() => setChecking(false), HANDOFF_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [checking]);

  useEffect(() => {
    if (applied.current) {
      return;
    }
    if (!handoff.present) {
      // An ad landing with only a click id or UTMs (`?gclid=...`) is not a
      // site hand-off, but on Aperio its campaign still belongs on every
      // event. (util/aperioTracking has already recorded it for the booking.)
      // Other tenants: unchanged, nothing happens here.
      applied.current = true;
      if (isAperioTrackingEnabled() && Object.keys(handoff.campaign).length) {
        mp.register(handoff.campaign);
      }
      return;
    }
    applied.current = true;

    // FIRST, before any event is tracked: adopt the marketing site's identity,
    // so everything this session sends can be joined to the page views that
    // led here. An anonymous device id becomes a super property; only a real
    // user id is identified (util/analytics adoptHandoffIdentity).
    if (handoff.mpId) {
      adoptHandoffIdentity(handoff.mpId, handoff.mpIsDevice);
    }

    // Remember where the visitor is, so the address box keeps ranking their
    // own city first even after a Back to this step strips the params.
    if (handoff.bias) {
      saveBiasHint(handoff.bias);
    }

    const campaignKeys = Object.keys(handoff.campaign);
    if (campaignKeys.length) {
      mp.register(handoff.campaign);
    }

    if (handoff.source) {
      try {
        window.sessionStorage.setItem(HANDOFF_SOURCE_KEY, handoff.source);
      } catch (e) {
        // sessionStorage unavailable (private mode) - attribution is best
        // effort only.
      }
      mp.register({ booking_source: handoff.source });
    }

    if (handoff.lang) {
      try {
        window.sessionStorage.setItem(HANDOFF_LANG_KEY, handoff.lang);
      } catch (e) {
        // best effort only
      }
      // <html lang> is NOT set from the hand-off any more: it has to name
      // the language actually on screen (QA 2026-09-25 #6: lang="fr" over
      // English copy). The Aperio preset's i18n sets it (i18n/index.ts);
      // every other tenant stays English and keeps index.html's "en".
    }

    if (handoff.address) {
      dispatch(setServiceAddress(['new', handoff.address]));
      dispatch(setPostalCode(handoff.address.postalCode));
    } else if (handoff.text) {
      // Only free text. Select the "new address" option so the seeded box is
      // the one shown, even for a signed-in visitor who has saved addresses...
      dispatch(setNewServiceAddress(undefined));
      // ...and then resolve that text ourselves rather than asking the visitor
      // to type it a second time. A resolved place is dispatched exactly as a
      // picked suggestion is, which fills in the postal code and lets the
      // service-area check (and the auto-advance) run as usual.
      resolveAddressText(handoff.text, PLACES_WAIT_MS, handoff.lang).then((place) => {
        if (!alive.current) {
          return;
        }
        if (place) {
          pending.current = true;
          dispatch(setNewServiceAddress(place));
        } else {
          // Nothing usable came back. Hand the page over to the visitor with
          // their text still in the box - the pre-existing behaviour.
          setNeedsManualPick(true);
          setChecking(false);
        }
        // Analytics is best effort here, and never a reason to break the
        // flow: this runs in a promise, where a throw would be an unhandled
        // rejection rather than something the visitor could recover from.
        try {
          mp.track(place
            ? 'Location - Handoff Address Resolved'
            : 'Location - Handoff Address Unresolved',
          { booking_source: handoff.source });
        } catch (e) {
          // analytics not initialised
        }
      });
    }

    // Drop only the hand-off params, leaving anything else on the URL alone.
    const next = new URLSearchParams(params);
    HANDOFF_PARAMS.forEach((key) => next.delete(key));
    setParams(next, { replace: true });
  }, [handoff, params, setParams, dispatch]);

  /**
   * True exactly once, for the route that is about to act on the hand-off.
   * Whatever the service-area answer was, the hand-off is over: the route
   * either navigates on or shows the not-in-service path, and either way the
   * "Checking your address..." state has to end.
   */
  const consumeHandoff = useCallback(() => {
    const was = pending.current;
    pending.current = false;
    setChecking(false);
    return was;
  }, []);

  return {
    prefilled: handoff.present,
    prefillText: handoff.text,
    source: handoff.source,
    lang: handoff.lang,
    /** Bias for the address box's suggestions, when the site gave one. */
    bias: handoff.bias,
    /** Google place_id of the address the site resolved, when it sent one. */
    placeId: handoff.placeId || null,
    /** Show a "Checking your address..." state instead of the address form. */
    checking,
    /** The lookup found nothing: focus the box and let the visitor pick. */
    needsManualPick,
    /** The site already reported the hand-off address's coverage (`cov=1`). */
    siteReported: handoff.siteReported,
    consumeHandoff,
  };
}
